import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Checkbox, 
  FormControlLabel,
  Stack,
  Divider,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LatexRenderer from './LatexRenderer';
import { marksFor } from '../utils/examDifficulty';

function Instructions({ onBackClick, onStartTestClick, examSettings }) {
  const [agreed, setAgreed] = useState(false);

  if (!examSettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <Paper sx={{ p: '32px', borderRadius: '12px', textAlign: 'center' }}>
          <Typography sx={{ mb: '24px', color: '#64748b', fontSize: '14px' }}>Loading exam details...</Typography>
          <Button variant="outlined" onClick={onBackClick} sx={{ textTransform: 'none' }}>Back</Button>
        </Paper>
      </Box>
    );
  }

  let subjectList = [];
  if (examSettings.subjects) {
    if (Array.isArray(examSettings.subjects)) {
      subjectList = examSettings.subjects.map((s, i) => ({
        name: s.name || `Subject ${i + 1}`,
        questions: Array.isArray(s) ? s.length : (s.questions || 25),
        chapter: s.chapter || null
      }));
    } else if (typeof examSettings.subjects === 'object') {
      subjectList = Object.entries(examSettings.subjects).map(([key, val]) => {
        const qCount = Array.isArray(val) ? val.length : (typeof val === 'object' ? val.questions : 25);
        const selCh = examSettings.chapterSelections ? examSettings.chapterSelections[key] : null;
        return {
          name: key,
          questions: qCount,
          chapter: selCh,
          topic: examSettings.topicSelections ? examSettings.topicSelections[key] : null
        };
      });
    }
  } else if (examSettings.subject) {
    subjectList = [{ name: examSettings.subject, questions: 30, chapter: examSettings.chapter || null }];
  }

  const totalQuestions = subjectList.reduce((sum, s) => sum + s.questions, 0);
  const marks = marksFor(examSettings);
  const totalMarks = totalQuestions * marks.correct;
  const examName = examSettings.name || (examSettings.examType ? `${examSettings.examType.toUpperCase()} Practice` : 'Test');
  const duration = examSettings.duration || 180;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: { xs: '16px', md: '40px' } }}>
      <Container maxWidth="md">
        <Paper sx={{ 
          p: { xs: '24px', md: '40px' }, 
          borderRadius: '16px', 
          border: '1px solid #e2e8f0', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          bgcolor: '#ffffff'
        }}>
          <Box sx={{ mb: '32px', textAlign: 'center', position: 'relative', px: { xs: '40px', md: '48px' } }}>
            <IconButton 
              onClick={onBackClick} 
              sx={{ position: 'absolute', top: 0, left: 0, color: '#64748b' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 800, color: '#1d4ed8', mb: '8px', fontSize: { xs: '22px', md: '30px' }, lineHeight: 1.25, textWrap: 'balance' }}>
              <LatexRenderer text={examName} />
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '15px' }}>Read the following instructions carefully.</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '32px', mb: '32px' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', fontSize: '11px', mb: '4px' }}>Time</Typography>
              <Typography sx={{ fontWeight: 700, color: '#334155', fontSize: '18px' }}>{duration} Min</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', fontSize: '11px', mb: '4px' }}>Max. Marks</Typography>
              <Typography sx={{ fontWeight: 700, color: '#334155', fontSize: '18px' }}>{totalMarks}</Typography>
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', mb: '32px', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '13px', py: '12px' }}>Subjects</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '13px', py: '12px' }} align="center">No. of Questions</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '13px', py: '12px' }} align="right">Marks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjectList.map((subject, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ textTransform: 'capitalize', fontSize: '13px', color: '#334155' }}>
                      <Box>
                        <LatexRenderer text={subject.name} />
                        {subject.chapter && (
                          <Typography sx={{ fontSize: '11px', color: '#64748b', mt: 0.5, fontStyle: 'italic' }}>
                            Chapter: {subject.chapter}{subject.topic ? ` · Topic: ${subject.topic}` : ''}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '13px', color: '#334155' }}>{subject.questions}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', color: '#334155' }}>{subject.questions * marks.correct}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#eff6ff' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#1d4ed8', fontSize: '13px' }}>Total</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#1d4ed8', fontSize: '13px' }}>{totalQuestions}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#1d4ed8', fontSize: '13px' }}>{totalMarks}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mb: '32px' }}>
            <Typography sx={{ fontWeight: 800, color: '#475569', mb: '16px', textTransform: 'uppercase', fontSize: '12px' }}>Guidelines</Typography>
            <Box component="ol" sx={{ pl: '16px', m: 0, '& li': { mb: '12px', color: '#334155', fontSize: '14px', lineHeight: 1.6 } }}>
              <li>{totalQuestions} questions will be there in total. {subjectList.length > 1 ? 'Detailed breakdown is shown in the table above.' : 'All questions are from the selected subject.'}</li>
              <li>Your answers shall be updated and saved on a server periodically and also at the end of the examination.</li>
              <li>Each question will have four choices out of which only ONE is the correct answer.</li>
              <li>
                You will be awarded {marks.correct} {marks.correct === 1 ? 'mark' : 'marks'} for each correct answer
                {marks.wrong < 0
                  ? ` and ${-marks.wrong} ${marks.wrong === -1 ? 'mark' : 'marks'} will be deducted for each wrong answer.`
                  : '. No marks are deducted for wrong answers.'}
              </li>
              <li>You can change your answer by clicking on some other option.</li>
              <li>You can access the questions in any order within a section or across sections.</li>
              <li>Do not click the button "submit test" before completing the test. A test once submitted cannot be resumed.</li>
            </Box>
          </Box>

          <Divider sx={{ mb: '32px', bgcolor: '#e2e8f0' }} />

          <Box sx={{ mb: '32px' }}>
            <FormControlLabel
              control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} color="primary" sx={{ p: '4px', mr: '8px' }} />}
              label={<Typography sx={{ fontSize: '14px', color: '#334155', fontWeight: 500 }}>I have read all the instructions carefully and have understood them.</Typography>}
              sx={{ m: 0 }}
            />
          </Box>

          <Stack direction="row" spacing="16px" justifyContent="flex-end">
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon sx={{ fontSize: '18px' }} />} 
              onClick={onBackClick}
              sx={{ borderRadius: '8px', textTransform: 'none', px: '24px', py: '8px', borderColor: '#cbd5e1', color: '#64748b', fontSize: '14px', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              disabled={!agreed}
              startIcon={<PlayArrowIcon sx={{ fontSize: '20px' }} />}
              onClick={() => onStartTestClick(examSettings)}
              sx={{ borderRadius: '8px', textTransform: 'none', px: '32px', py: '10px', bgcolor: '#1d4ed8', fontWeight: 700, fontSize: '14px', '&:hover': { bgcolor: '#1e40af' } }}
            >
              Start Test
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default Instructions;

